import {
  Box,
  Button,
  Card,
  CardMedia,
  Grid,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import Slider from "react-slick";

import PageSection from "components/shared/PageSection";
import { usePageContext } from "contexts/PageContextProvider";
import LazyLoad from "react-lazy-load";
// assets
import JoinUsImage from "assets/images/group-pink-2023-10-27-min.png";

/**
 * * Team section of the Homepage
 * @returns {React.ReactElement} Team
 */
export default function Team() {
  const theme = useTheme();
  return (
    <PageSection
      id="team-section"
      backgroundSx={{
        background: `radial-gradient(ellipse at top left, ${theme.palette.secondary.main} 40%, #fff 100%)`,
      }}
    >
      <SectionText />
      <TeamCarousel />
      <JoinUsBox />
    </PageSection>
  );
}

/**
 * * Text for the Team section
 * @returns {React.ReactElement} SectionText
 */
function SectionText() {
  return (
    <Box>
      <Typography variant="h2">The team</Typography>
      <Typography variant="body1" py={3}>
        The greatest solutions to the most difficult challenges come from
        passionate humans working together to find answers that generate
        impactful change. We built the Astrin team with this in mind. We truly
        believe that it is the blending of distinctive backgrounds and life
        experience that yield the most unexpected and significant results. Our
        team is widely published, offering their insight and expertise in more
        than 500 papers on topics from holographic imaging to proteomics and
        cancer signaling.
      </Typography>
    </Box>
  );
}

/**
 * * Carousel of team members
 * @returns {React.ReactElement} TeamCarousel
 */
function TeamCarousel() {
  const { people, peopleBackgrounds, lazyLoadOffset } = usePageContext();

  const greaterThanMd = useMediaQuery((theme) => theme.breakpoints.up("md"));
  const greaterThanLg = useMediaQuery((theme) => theme.breakpoints.up("lg"));
  const visibleSlides = greaterThanMd ? (greaterThanLg ? 4 : 3) : 2;

  return (
    <Box px={{ xs: 2, md: 0 }} py={{ xs: 0, sm: 1 }}>
      <LazyLoad offset={lazyLoadOffset}>
        <Slider
          dots={greaterThanMd}
          slidesToShow={visibleSlides}
          slidesToScroll={visibleSlides}
        >
          {people.map((person, index) => (
            <Card
              key={index}
              sx={{
                borderRadius: 1,
                backgroundColor: "#fff0",
                overflow: "hidden",
              }}
              elevation={0}
            >
              <Box px={{ xs: 0.5, sm: 1.5 }}>
                <Box
                  overflow="hidden"
                  sx={{
                    backgroundImage: `url(${
                      peopleBackgrounds[index % peopleBackgrounds.length].image
                    })`,
                    backgroundPositionX:
                      peopleBackgrounds[index % peopleBackgrounds.length]
                        .offsetX,
                    backgroundPositionY:
                      peopleBackgrounds[index % peopleBackgrounds.length]
                        .offsetY,
                    borderRadius: 1,
                  }}
                >
                  <Button
                    href={person.link}
                    target="_blank"
                    sx={{ p: 0, m: 0 }}
                  >
                    <CardMedia component="img" src={`${person.image}`} />
                  </Button>
                </Box>
                <Box align="center" pt={1}>
                  <Box
                    borderRadius={0}
                    display="flex"
                    flexDirection="column"
                    sx={{ backgroundColor: "#fff", borderRadius: 1 }}
                  >
                    <Typography
                      variant="caption"
                      color="secondary"
                      fontWeight="bold"
                    >
                      {person.name}
                    </Typography>
                    <Typography variant="caption" color="secondary">
                      {person.position}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Card>
          ))}
        </Slider>
      </LazyLoad>
    </Box>
  );
}

/**
 * * Join us section
 * @returns {React.ReactElement} JoinUsBox
 */
function JoinUsBox() {
  const theme = useTheme();
  const { lazyLoadOffset } = usePageContext();
  return (
    <Box
      mt={10}
      p={2}
      sx={{
        borderRadius: 1,
        overflow: "hidden",
        background: `radial-gradient(ellipse at bottom right, ${theme.palette.secondary.main} 0%, #eeeeee 100%)`,
      }}
    >
      <Grid container spacing={1}>
        <Grid item xs={12} md={6} lg={7}>
          <Box p={2} height="100%" display="flex" flexDirection="column">
            <Typography variant="h3" color="primary" fontWeight="bold">
              Join us!
            </Typography>
            <Typography variant="subtitle1" color="secondary">
              We're looking for creative, hard working, enthusiastic team
              members with a passion for their field and for the cause.
            </Typography>
          </Box>
        </Grid>
        <Grid item xs={12} md={6} lg={5}>
          <Box align="center">
            <LazyLoad offset={lazyLoadOffset}>
              <Box sx={{ borderRadius: 1, overflow: "hidden" }}>
                <img
                  src={JoinUsImage}
                  alt="lab techs"
                  height="100%"
                  width="100%"
                />
              </Box>
            </LazyLoad>
            <Typography variant="caption" color="inherit">
              The crew in pink for Breast Cancer Awareness Day
            </Typography>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
}
